import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db, auth } from '../firebase'; // Assuming firebase is set up
import Header from '../components/Header';
import Footer from '../components/Footer';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// We'll need the MCHAT_QUESTIONS again to display them, or pass them in props/context
// For simplicity, redefining here, but ideally, this would be a shared utility
const MCHAT_QUESTIONS = [
    { id: 1, text: "Does your child enjoy being swung, bounced on your knee, etc.?", risk_answer: "no" },
    { id: 2, text: "Does your child take an interest in other children?", risk_answer: "no" },
    { id: 3, text: "Does your child like climbing on things, such as stairs?", risk_answer: "no" },
    { id: 4, text: "Does your child enjoy playing peek-a-boo/hide-and-seek?", risk_answer: "no" },
    { id: 5, text: "Does your child ever pretend, for example, to talk on the phone or take care of a doll or anything else?", risk_answer: "no" },
    { id: 6, text: "Does your child ever use his/her index finger to point, to ask for something?", risk_answer: "no" },
    { id: 7, text: "Does your child ever use his/her index finger to point, to indicate interest in something?", risk_answer: "no" },
    { id: 8, text: "Can your child play properly with small toys (e.g. cars or bricks) without just mouthing, fiddling, or dropping them?", risk_answer: "no" },
    { id: 9, text: "Does your child ever bring objects over to you (parent) to show you something?", risk_answer: "no" },
    { id: 10, text: "Does your child look you in the eye for more than a second or two?", risk_answer: "no" },
    { id: 11, text: "Does your child ever seem oversensitive to noise? (e.g., plugging ears)", risk_answer: "yes" },
    { id: 12, text: "Does your child smile in response to your face or your smile?", risk_answer: "no" },
    { id: 13, text: "Does your child imitate you? (e.g., you make a face-will your child imitate it?)", risk_answer: "no" },
    { id: 14, text: "Does your child respond to his/her name when you call?", risk_answer: "no" },
    { id: 15, text: "If you point at a toy across the room, does your child look at it?", risk_answer: "no" },
    { id: 16, text: "Does your child walk?", risk_answer: "no" },
    { id: 17, text: "Does your child look at things you are looking at?", risk_answer: "no" },
    { id: 18, text: "Does your child make unusual finger movements near his/her face?", risk_answer: "yes" },
    { id: 19, text: "Does your child try to attract your attention to his/her activity?", risk_answer: "no" },
    { id: 20, text: "Have you ever wondered if your child is deaf?", risk_answer: "yes" },
  ];

const AssessmentReport = () => {
  const { assessmentId } = useParams();
  const [assessmentData, setAssessmentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [user, setUser] = useState(null);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!assessmentId) {
      setError('No assessment ID provided.');
      setLoading(false);
      return;
    }

    const fetchAssessment = async () => {
      try {
        if (!user) {
          setError('Please log in to view your assessment reports.');
          setLoading(false);
          return;
        }

        // Use the correct path that matches our security rules: users/{userId}/assessments/{assessmentId}
        const docRef = doc(db, 'users', user.uid, 'assessments', assessmentId);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          // Additional security check to ensure the assessment belongs to the user
          if (data.userId === user.uid) {
            setAssessmentData(data);
          } else {
            setError('You do not have permission to view this report.');
          }
        } else {
          setError('Assessment not found. It may have been deleted or you may not have access to it.');
        }
      } catch (err) {
        console.error('Error fetching assessment:', err);
        setError('Failed to load assessment data. Please try again later.');
      }
      setLoading(false);
    };

    // Wait for user auth state to be determined before fetching
    if (user !== null) {
      fetchAssessment();
    } else if (auth.currentUser) {
      // If auth state resolves quickly, update user state and fetch
      setUser(auth.currentUser);
      // No need to call fetchAssessment here as the effect will re-run with the new user state
    }
  }, [assessmentId, user]); // Re-run if assessmentId or user changes

  const handleDownloadPdf = () => {
    if (!assessmentData) return;
    const doc = new jsPDF();

    // Title
    doc.setFontSize(18);
    doc.text('M-CHAT-R/F Assessment Report', 14, 18);

    // Date
    doc.setFontSize(11);
    doc.setTextColor(100);
    const assessmentDate = assessmentData.timestamp
      ? new Date(assessmentData.timestamp.seconds * 1000).toLocaleDateString()
      : 'N/A';
    doc.text(`Date: ${assessmentDate}`, 14, 26);

    // Risk Level & Score
    doc.setFontSize(13);
    doc.setTextColor(30, 58, 138);
    doc.text(`Risk Level: ${assessmentData.riskLevel || 'Not Calculated'}`, 14, 36);
    doc.text(
      `Score: ${assessmentData.score} / ${MCHAT_QUESTIONS.length}` +
        (typeof assessmentData.criticalFails === 'number' && assessmentData.criticalFails > 0
          ? ` (${assessmentData.criticalFails} critical item(s) failed)`
          : ''),
      14,
      44
    );

    // Summary
    doc.setFontSize(12);
    doc.setTextColor(33, 37, 41);
    doc.text('AI Generated Summary:', 14, 54);
    doc.setFontSize(11);
    doc.text(assessmentData.summary || 'Summary generation is pending or was not requested.', 14, 62, { maxWidth: 180 });

    // Questions & Answers Table
    const tableData = MCHAT_QUESTIONS.map((q, i) => [
      i + 1,
      q.text,
      assessmentData.answers[q.id] || 'Not answered',
      assessmentData.answers[q.id] === q.risk_answer ? '⚠️' : ''
    ]);
    autoTable(doc, {
      startY: 70,
      head: [['#', 'Question', 'Your Answer', 'At Risk?']],
      body: tableData,
      styles: { fontSize: 9, cellWidth: 'wrap' },
      columnStyles: { 1: { cellWidth: 90 } }
    });

    // Disclaimer
    doc.setFontSize(9);
    doc.setTextColor(120);
    doc.text(
      'Disclaimer: This screening tool is for informational purposes and not a diagnostic test. Consult a healthcare professional for diagnosis.',
      14,
      doc.lastAutoTable ? doc.lastAutoTable.finalY + 10 : 280,
      { maxWidth: 180 }
    );

    doc.save('MCHAT-Assessment-Report.pdf');
  };

  if (loading) {
    return (
        <>
            <Header />
            <div className="min-h-[calc(100vh-160px)] flex items-center justify-center bg-gray-100 dark:bg-slate-900">
                <p className="text-xl text-gray-700 dark:text-gray-300">Loading report...</p>
            </div>
            <Footer />
        </>
    );
  }

  if (error) {
    return (
        <>
            <Header />
            <div className="min-h-[calc(100vh-160px)] flex flex-col items-center justify-center bg-red-50 dark:bg-red-900/20 p-6">
                <p className="text-xl text-red-600 dark:text-red-400">{error}</p>
                <Link to="/app/dashboard" className="mt-4 px-4 py-2 bg-sky-500 text-white rounded hover:bg-sky-600">
                    Go to Dashboard
                </Link>
            </div>
            <Footer />
        </>
    );
  }

  if (!assessmentData) {
    // This case should ideally be covered by loading or error states
    return (
        <>
            <Header />
            <div className="min-h-[calc(100vh-160px)] flex items-center justify-center bg-gray-100 dark:bg-slate-900">
                <p className="text-xl text-gray-700 dark:text-gray-300">No report data available.</p>
            </div>
            <Footer />
        </>
    );
  }

  // Convert timestamp to readable date
  const assessmentDate = assessmentData.timestamp ? new Date(assessmentData.timestamp.seconds * 1000).toLocaleDateString() : 'N/A';

  return (
    <>
      <Header />
      <div className="min-h-[calc(100vh-160px)] bg-gradient-to-b from-sky-50 to-white dark:from-sky-900 dark:to-slate-800 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto bg-white dark:bg-slate-700 p-6 sm:p-10 rounded-xl shadow-2xl border border-sky-200 dark:border-sky-700">
          <h1 className="text-2xl sm:text-3xl font-bold text-sky-700 dark:text-sky-300 mb-2">M-CHAT-R/F Assessment Report</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">Date: {assessmentDate}</p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-sky-100 dark:bg-sky-800 p-4 rounded-lg shadow">
              <h2 className="text-lg font-semibold text-sky-700 dark:text-sky-300 mb-1">Risk Level</h2>
              <p className={`text-xl font-bold ${assessmentData.riskLevel?.includes('High') ? 'text-red-500' : assessmentData.riskLevel?.includes('Medium') ? 'text-yellow-500' : 'text-green-500'}`}>
                {assessmentData.riskLevel || 'Not Calculated'}
              </p>
            </div>
            <div className="bg-sky-100 dark:bg-sky-800 p-4 rounded-lg shadow">
              <h2 className="text-lg font-semibold text-sky-700 dark:text-sky-300 mb-1">Score</h2>
              <p className="text-xl font-bold text-gray-800 dark:text-gray-200">
                {assessmentData.score} / {MCHAT_QUESTIONS.length}
                {typeof assessmentData.criticalFails === 'number' && assessmentData.criticalFails > 0 && (
                    <span className="text-sm ml-2 text-yellow-600 dark:text-yellow-400">({assessmentData.criticalFails} critical item(s) failed)</span>
                )}
              </p>
            </div>
          </div>

          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-3">AI Generated Summary</h2>
            <div className="bg-gray-50 dark:bg-slate-600 p-4 rounded-md min-h-[80px]">
              <p className="text-gray-700 dark:text-gray-300">
                {assessmentData.summary || 'Summary generation is pending or was not requested.'}
              </p>
            </div>
          </div>

          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-3">Your Answers:</h2>
            <div className="space-y-3">
              {MCHAT_QUESTIONS.map((q, index) => (
                <div key={q.id} className="p-3 bg-white dark:bg-slate-600 rounded-md border border-gray-200 dark:border-slate-500">
                  <p className="font-medium text-gray-700 dark:text-gray-300">{index + 1}. {q.text}</p>
                  <p className={`capitalize font-semibold ${assessmentData.answers[q.id] === q.risk_answer ? 'text-red-500' : 'text-green-500'}`}>
                    Your answer: {assessmentData.answers[q.id] || 'Not answered'}
                  </p>
                </div>
              ))}
            </div>
          </div>
          
          <div className="text-center mt-10">
            <button 
              onClick={handleDownloadPdf}
              className="px-8 py-3 bg-green-500 hover:bg-green-600 text-white font-bold text-lg rounded-lg shadow-md transition-transform transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-green-400 focus:ring-opacity-75"
            >
              Download PDF Report 📄
            </button>
          </div>

           <p className="mt-8 text-xs text-gray-500 dark:text-gray-400 text-center">
            Disclaimer: This screening tool is for informational purposes and not a diagnostic test. Consult a healthcare professional for diagnosis.
          </p>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default AssessmentReport;
